import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, ArrowLeft, Plane, User, Phone, Building2, CheckCircle, Receipt, MapPin, Upload, FileText, X } from 'lucide-react';
import axios from 'axios';
import PhoneInput, { isValidPhoneNumber } from '../components/ui/PhoneInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const MAX_KYC_BYTES = 5 * 1024 * 1024;
const PAN_CARD_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const GST_CERT_EXTENSIONS = ['.pdf'];

function fileExtension(file) {
  const name = file?.name || '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function validateKycFile(file, allowedExts, label, required = true) {
  if (!file) {
    return required ? `${label} is required` : null;
  }
  const ext = fileExtension(file);
  if (!allowedExts.includes(ext)) {
    return `${label}: allowed formats are ${allowedExts.join(', ')}`;
  }
  if (file.size > MAX_KYC_BYTES) {
    return `${label} must be 5 MB or smaller`;
  }
  return null;
}

function formatApiError(detail) {
  if (!detail) return 'Registration failed. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        const field = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
        return field ? `${field}: ${item.msg}` : item.msg;
      })
      .join(' ');
  }
  return 'Registration failed. Please try again.';
}

const AgentSignup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    agencyName: '',
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // GST block — default OFF so existing flow is untouched for walk-ins
    hasGstin: false,
    gstin: '',
    pan: '',
    stateCode: '',
    billingAddress: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [states, setStates] = useState([]);
  const [panCardFile, setPanCardFile] = useState(null);
  const [gstCertFile, setGstCertFile] = useState(null);

  // Fetch India GST state codes for the dropdown
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/agent/gst-states`)
      .then((r) => setStates(r.data?.states || []))
      .catch(() => setStates([]));
  }, []);

  const selectedStateName = states.find((s) => s.code === formData.stateCode)?.name || '';

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.agencyName) {
      newErrors.agencyName = 'Agency name is required';
    }
    
    if (!formData.fullName) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number with country code';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    const panErr = validateKycFile(panCardFile, PAN_CARD_EXTENSIONS, 'PAN card');
    if (panErr) newErrors.panCard = panErr;

    if (formData.hasGstin) {
      const gstErr = validateKycFile(gstCertFile, GST_CERT_EXTENSIONS, 'GST certificate');
      if (gstErr) newErrors.gstCertificate = gstErr;
    }

    // GST validation (only when the checkbox is ticked)
    if (formData.hasGstin) {
      const g = (formData.gstin || '').toUpperCase().trim();
      const gstinRe = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
      if (!g) {
        newErrors.gstin = 'GSTIN is required when opting for GST invoice';
      } else if (!gstinRe.test(g)) {
        newErrors.gstin = 'Invalid GSTIN format (15 chars, e.g. 27ABCDE1234F1Z5)';
      } else if (formData.stateCode && g.slice(0, 2) !== formData.stateCode) {
        newErrors.gstin = `GSTIN prefix (${g.slice(0, 2)}) must match selected state (${formData.stateCode})`;
      }
      if (!formData.stateCode) {
        newErrors.stateCode = 'Select your billing state';
      }
      const p = (formData.pan || '').toUpperCase().trim();
      if (!p) {
        newErrors.pan = 'PAN is required for GST invoicing';
      } else if (!PAN_REGEX.test(p)) {
        newErrors.pan = 'Invalid PAN format (e.g. ABCDE1234F)';
      }
      if (!formData.billingAddress || formData.billingAddress.trim().length < 10) {
        newErrors.billingAddress = 'Billing address is required for GST invoicing';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const fd = new FormData();
      fd.append('agency_name', formData.agencyName);
      fd.append('full_name', formData.fullName);
      fd.append('email', formData.email.trim().toLowerCase());
      fd.append('phone', formData.phone.trim());
      fd.append('password', formData.password);
      fd.append('has_gstin', formData.hasGstin ? 'true' : 'false');
      fd.append('pan_card', panCardFile);

      if (formData.hasGstin) {
        fd.append('gstin', formData.gstin.toUpperCase().trim());
        fd.append('pan', (formData.pan || '').toUpperCase().trim());
        fd.append('state_code', formData.stateCode);
        fd.append('state', selectedStateName);
        fd.append('billing_address', formData.billingAddress.trim());
        fd.append('gst_certificate', gstCertFile);
      } else if (formData.billingAddress) {
        fd.append('billing_address', formData.billingAddress.trim());
      }

      await axios.post(`${BACKEND_URL}/api/agent/register`, fd);

      setSuccess(true);
      
    } catch (error) {
      console.error('Agent signup error:', error);
      setErrors({
        general: formatApiError(error.response?.data?.detail),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen travel-bg flex items-center justify-center p-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/agent/login')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#1B375B] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Login</span>
        </button>

        {/* Signup Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#1B375B] to-[#659A9B] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Plane className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#1B375B] mb-2">Create Agent Account</h1>
            <p className="text-gray-500 text-sm">Join our network of travel professionals</p>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSignup} className="space-y-5">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Registration Successful!</p>
                  <p className="text-xs text-green-700 mt-1">
                    Your application is pending admin approval. We&apos;ve sent a confirmation email —
                    you&apos;ll receive another email once your account is approved. Login will be
                    available after approval.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/agent/login')}
                    className="mt-3 text-xs font-semibold text-[#659A9B] hover:text-[#1B375B] transition-colors"
                  >
                    Go to login page
                  </button>
                </div>
              </div>
            )}

            {/* General Error */}
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 text-center">{errors.general}</p>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-5">
              {/* Agency Name */}
              <div className="md:col-span-2">
                <label htmlFor="agencyName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Agency Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Building2 className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="agencyName"
                    name="agencyName"
                    type="text"
                    value={formData.agencyName}
                    onChange={handleChange}
                    placeholder="ABC Travel Agency"
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.agencyName
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                    }`}
                  />
                </div>
                {errors.agencyName && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.agencyName}
                  </p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.fullName
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                    }`}
                  />
                </div>
                {errors.fullName && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneInput
                    value={formData.phone}
                    onChange={(v) => setFormData((prev) => ({ ...prev, phone: v }))}
                    defaultCountry="IN"
                    placeholder="Mobile number"
                    testid="agent-signup-phone"
                    className={errors.phone ? 'ring-1 ring-red-300 rounded-lg' : ''}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="agent@example.com"
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.email
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min. 8 characters"
                    className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.password
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter password"
                    className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* PAN card upload — required for all registrations */}
            <div className="rounded-2xl border-2 border-gray-200 bg-gray-50 p-5" data-testid="agent-signup-pan-card-block">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-[#659A9B]" />
                <span className="font-semibold text-[#1B375B]">PAN Card</span>
                <span className="text-xs text-red-600 font-medium">Required</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">Upload a clear copy of your agency PAN card (PDF, JPG, or PNG — max 5 MB).</p>
              <label className={`flex flex-col items-center justify-center w-full py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                errors.panCard ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-white hover:border-[#659A9B] hover:bg-[#F5FBFB]'
              }`}>
                <Upload className="w-6 h-6 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {panCardFile ? panCardFile.name : 'Click to upload PAN card'}
                </span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  className="hidden"
                  data-testid="agent-signup-pan-card"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setPanCardFile(f);
                    if (errors.panCard) setErrors((prev) => ({ ...prev, panCard: '' }));
                  }}
                />
              </label>
              {panCardFile && (
                <button
                  type="button"
                  onClick={() => setPanCardFile(null)}
                  className="mt-2 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Remove file
                </button>
              )}
              {errors.panCard && <p className="mt-1.5 text-xs text-red-600">{errors.panCard}</p>}
            </div>

            {/* GST Invoicing (optional) */}
            <div className={`rounded-2xl border-2 transition-all ${formData.hasGstin ? 'border-[#659A9B] bg-[#F5FBFB]' : 'border-gray-200 bg-gray-50'}`} data-testid="agent-signup-gst-block">
              <label className="flex items-start gap-3 p-5 cursor-pointer">
                <input
                  type="checkbox"
                  name="hasGstin"
                  checked={formData.hasGstin}
                  onChange={(e) => {
                    handleChange(e);
                    if (!e.target.checked) setGstCertFile(null);
                  }}
                  className="mt-1 w-5 h-5 text-[#659A9B] border-gray-300 rounded focus:ring-[#659A9B]"
                  data-testid="agent-signup-has-gstin"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-[#659A9B]" />
                    <span className="font-semibold text-[#1B375B]">I need GST-compliant invoices</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Tick this if your agency has a valid GSTIN. You&apos;ll receive proper B2B tax invoices
                    (with CGST / SGST / IGST breakdown) for every booking. Contact admin to update
                    GST details after registration.
                  </p>
                </div>
              </label>

              {formData.hasGstin && (
                <div className="px-5 pb-5 pt-1 space-y-4 border-t border-[#659A9B]/20">
                  {/* GSTIN */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">GSTIN</label>
                    <input
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleChange}
                      maxLength={15}
                      placeholder="e.g. 27ABCDE1234F1Z5"
                      data-testid="agent-signup-gstin"
                      className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 uppercase font-mono tracking-wide focus:outline-none focus:ring-2 transition-all ${
                        errors.gstin ? 'border-red-300 focus:ring-red-200'
                                     : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                      }`}
                    />
                    {errors.gstin && <p className="mt-1.5 text-xs text-red-600">{errors.gstin}</p>}
                  </div>

                  {/* State + PAN (2-col) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Billing State</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <select
                          name="stateCode"
                          value={formData.stateCode}
                          onChange={handleChange}
                          data-testid="agent-signup-state"
                          className={`w-full pl-12 pr-4 py-3 bg-white border rounded-xl text-gray-900 focus:outline-none focus:ring-2 transition-all appearance-none ${
                            errors.stateCode ? 'border-red-300 focus:ring-red-200'
                                             : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                          }`}
                        >
                          <option value="">Select state…</option>
                          {states.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name} ({s.code})
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors.stateCode && <p className="mt-1.5 text-xs text-red-600">{errors.stateCode}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        PAN
                      </label>
                      <input
                        name="pan"
                        value={formData.pan}
                        onChange={handleChange}
                        maxLength={10}
                        placeholder="ABCDE1234F"
                        data-testid="agent-signup-pan"
                        className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 uppercase font-mono tracking-wide focus:outline-none focus:ring-2 transition-all ${
                          errors.pan ? 'border-red-300 focus:ring-red-200'
                                     : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                        }`}
                      />
                      {errors.pan && <p className="mt-1.5 text-xs text-red-600">{errors.pan}</p>}
                    </div>
                  </div>

                  {/* Billing address */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Billing Address (as registered on GSTIN)
                    </label>
                    <textarea
                      name="billingAddress"
                      value={formData.billingAddress}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Shop/Office, Street, City, PIN"
                      data-testid="agent-signup-billing-address"
                      className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 transition-all ${
                        errors.billingAddress ? 'border-red-300 focus:ring-red-200'
                                              : 'border-gray-200 focus:ring-[#659A9B]/30 focus:border-[#659A9B]'
                      }`}
                    />
                    {errors.billingAddress && <p className="mt-1.5 text-xs text-red-600">{errors.billingAddress}</p>}
                  </div>

                  {/* GST certificate upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      GST Registration Certificate <span className="text-red-600">*</span>
                    </label>
                    <p className="text-xs text-gray-600 mb-2">PDF only — max 5 MB</p>
                    <label className={`flex flex-col items-center justify-center w-full py-5 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                      errors.gstCertificate ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-white hover:border-[#659A9B]'
                    }`}>
                      <Upload className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-600">
                        {gstCertFile ? gstCertFile.name : 'Click to upload GST certificate'}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        data-testid="agent-signup-gst-certificate"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setGstCertFile(f);
                          if (errors.gstCertificate) setErrors((prev) => ({ ...prev, gstCertificate: '' }));
                        }}
                      />
                    </label>
                    {gstCertFile && (
                      <button
                        type="button"
                        onClick={() => setGstCertFile(null)}
                        className="mt-2 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Remove file
                      </button>
                    )}
                    {errors.gstCertificate && <p className="mt-1.5 text-xs text-red-600">{errors.gstCertificate}</p>}
                  </div>

                  <p className="text-[11px] text-gray-500 bg-white/60 rounded-lg px-3 py-2 border border-[#659A9B]/20">
                    ✓ Your GSTIN is verified against the state prefix. Input Tax Credit claims depend on
                    accurate details — double-check before submitting.
                  </p>
                </div>
              )}
            </div>

            {/* Terms */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs text-gray-600 leading-relaxed">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="text-[#659A9B] font-semibold hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#659A9B] font-semibold hover:underline">Privacy Policy</Link>
              </p>
            </div>

            {/* Signup Button */}
            <button
              type="submit"
              disabled={isLoading || success}
              className="w-full py-4 bg-gradient-to-r from-[#1B375B] to-[#335E77] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="text-center mt-6">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/agent/login')}
                className="text-[#659A9B] font-semibold hover:text-[#1B375B] transition-colors"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            Secure registration with 256-bit encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentSignup;
